import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * Where a workspace's Back goes: the list view the user opened the record from, filters and page
 * included, or the bare list when there is none.
 *
 * The lists keep their filters in the query string, so "the view" is just the URL. It is kept in
 * sessionStorage rather than in router state because a workspace replaces its own URL when a tab
 * changes, which drops router state, and a reload drops it too. A record opened from a bookmark
 * falls back to the bare list, which is what every other workspace's Back does.
 */

const KEY = 'cc-list-view:';

/** Only a URL on the list's own path is ever returned, so nothing stored can send Back elsewhere. */
export const lastListView = (listPath: string, storage: Pick<Storage, 'getItem'> | null = safeStorage()): string => {
  let stored: string | null = null;
  try {
    stored = storage?.getItem(KEY + listPath) ?? null;
  } catch {
    stored = null;
  }
  return stored && (stored === listPath || stored.startsWith(`${listPath}?`)) ? stored : listPath;
};

export const rememberListView = (
  listPath: string,
  url: string,
  storage: Pick<Storage, 'setItem'> | null = safeStorage(),
): void => {
  try {
    storage?.setItem(KEY + listPath, url);
  } catch {
    /* Unavailable storage only means Back lands on the unfiltered list. */
  }
};

function safeStorage(): Storage | null {
  try {
    return typeof window === 'undefined' ? null : window.sessionStorage;
  } catch {
    return null;
  }
}

/** Mount on a list page: records its current URL every time the filters or page change. */
export const useRememberListView = (listPath: string): void => {
  const { pathname, search } = useLocation();
  useEffect(() => {
    if (pathname === listPath) rememberListView(listPath, `${pathname}${search}`);
  }, [listPath, pathname, search]);
};
